import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import {
    OrdersService,
    DiscountType,
    GetOrderResDto,
    BookingsService,
    GetEventResBookingsItem0Dto,
    GetEventResBookingsItem1Dto,
    CreateBookingResFinancialsDto,
    CancelOrdersResDto,
    GetOrdersResDto,
} from '../../generated';
import { getOrderItemDtoToOrder } from './order.converters';
import {
    CustomInfiniteQueryOptions,
    CustomMutationOptions,
    CustomQueryOptions,
} from '../common.types';
import {
    BookingPriceParams,
    BookingPriceSummary,
} from '../booking/booking.types';
import {
    convertBookingPriceParamsAndPriceResponseToBookingPriceSummary,
    convertBookingPriceParamsToCreateBookingReqItemDto,
} from '../booking/booking.converters';
import { UseOrdersData } from './order.types';

export enum RequestTypes {
    EVENT = 'event',
    ORDER = 'order',
}

export const useCancelOrder = (
    options?: CustomMutationOptions<
        { orderId: string; reason?: string },
        CancelOrdersResDto
    >
) => {
    return useMutation({
        mutationFn: async ({ orderId, reason }) => {
            const response = await OrdersService.cancelOrders({
                path: { order_id: orderId },
                body: {
                    message: reason || '',
                },
            });

            if (!response.data) throw new Error('Failed to cancel order');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            // TODO update order in cache
            options?.onSuccess?.(...args);
        },
    });
};

export const useSendOrder = (
    options?: CustomMutationOptions<{ orderId: string; reason?: string }, any>
) => {
    return useMutation({
        mutationFn: async ({ orderId }) => {
            const response = await OrdersService.sendOrderCheckoutUrl({
                path: { order_id: orderId },
                query: { send_email: true },
            });

            if (!response.data) throw new Error('Failed to cancel order');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            // TODO update order in cache
            options?.onSuccess?.(...args);
        },
    });
};

export const useCopyOrderPublic = (
    options?: CustomMutationOptions<{ orderId: string; reason?: string }, any>
) => {
    return useMutation({
        mutationFn: async ({ orderId }) => {
            const response = await OrdersService.sendOrderCheckoutUrl({
                path: { order_id: orderId },
                query: { send_email: false },
            });
            const url = (response.data as any)?.url;

            if (!url) throw new Error('Failed to get order URL');

            return url;
        },
        ...options,
        onSuccess: (...args) => {
            // TODO update order in cache
            options?.onSuccess?.(...args);
        },
    });
};

export const useUpdatePrice = (
    params?: BookingPriceParams,
    initialPrice?: {
        total: number;
        subtotal: number;
    },
    options?: CustomMutationOptions<
        BookingPriceParams,
        CreateBookingResFinancialsDto
    >
) => {
    const [state, setState] = useState<{
        data?: BookingPriceSummary;
        params?: BookingPriceParams;
        isLoading: boolean;
        discount: number;
        discountType: DiscountType;
        options: string[];
    }>({
        data: params
            ? {
                  ...convertBookingPriceParamsAndPriceResponseToBookingPriceSummary(
                      params
                  ),
                  beforeDiscount: initialPrice?.subtotal || 0,
                  final: initialPrice?.total || 0,
              }
            : undefined,
        params,
        isLoading: true,
        discount: 0,
        discountType: DiscountType.RELATIVE,
        options: [],
    });
    const { mutate: getPrices } = useMutation({
        mutationFn: async priceParams => {
            const response = await BookingsService.computePriceBookings({
                body: convertBookingPriceParamsToCreateBookingReqItemDto(
                    priceParams
                ),
            });

            if (!response.data) throw new Error('Failed to compute price');

            return response.data;
        },
        ...options,
        onSuccess: (data, priceParams) => {
            setState(prevData => ({
                ...prevData,
                data: convertBookingPriceParamsAndPriceResponseToBookingPriceSummary(
                    priceParams,
                    data
                ),
                isLoading: false,
            }));
        },
    });

    const needsUpdate = useMemo(() => {
        const initialPriceValid =
            params &&
            initialPrice &&
            !params?.options.length &&
            !params?.discount?.amount;
        const oldParams = state.params
            ? JSON.stringify(
                  convertBookingPriceParamsToCreateBookingReqItemDto(
                      state.params
                  )
              )
            : '';
        const newParams = params
            ? JSON.stringify(
                  convertBookingPriceParamsToCreateBookingReqItemDto(params)
              )
            : '';

        return !initialPriceValid && oldParams !== newParams;
    }, [state.params, params]);

    React.useEffect(() => {
        if (
            params &&
            initialPrice &&
            !params?.options.length &&
            !params?.discount?.amount
        ) {
            setState({
                data: {
                    ...convertBookingPriceParamsAndPriceResponseToBookingPriceSummary(
                        params
                    ),
                    beforeDiscount: initialPrice.subtotal,
                    final: initialPrice.total,
                },
                params,
                isLoading: false,
                discount: 0,
                discountType: DiscountType.RELATIVE,
                options: [],
            });
        }
    }, [params, initialPrice]);

    const update = useCallback(() => {
        if (params?.pax) {
            setState(prevState => ({
                ...prevState,
                isLoading: true,
                params,
            }));
            getPrices(params);
        }
    }, [params, getPrices]);

    return {
        data: state.data,
        isLoading: state.isLoading,
        changed: needsUpdate,
        update,
    };
};

const useOrdersKey = 'useOrdersKey';
export const useOrders = (
    params: UseOrdersData = {},
    options: CustomQueryOptions<GetOrdersResDto> = {}
) => {
    const newParams = { ...params, pageSize: 1000 };
    const { data, ...other } = useQuery({
        queryKey: [useOrdersKey, newParams],
        queryFn: async () => {
            const response = await OrdersService.getOrders({
                query: {
                    ...(params.action && { action: params.action }),
                    ...(params.at_start && { at_start: params.at_start }),
                    ...(params.at_end && { at_end: params.at_end }),
                    ...(params.by && { by: params.by }),
                    ...(params.statuses && { statuses: params.statuses }),
                    ...(params.text_search && {
                        text_search: params.text_search,
                    }),
                    ...(params.bookings !== undefined && {
                        bookings: params.bookings,
                    }),
                    ...(newParams.pageNumber !== undefined && {
                        page_number: newParams.pageNumber,
                    }),
                    ...(newParams.pageSize && {
                        page_size: newParams.pageSize || 1000,
                    }),
                },
            });

            if (!response.data) throw new Error('Failed to get orders');

            return response.data;
        },
        ...options,
    });

    const parsedData = useMemo(
        () => data?.items?.map(getOrderItemDtoToOrder),
        [data]
    );

    return {
        data: parsedData,
        ...other,
    };
};

const useOrdersLazyKey = 'useOrdersLazyKey';
export const useOrdersLazy = (
    params: UseOrdersData = {},
    options: CustomInfiniteQueryOptions<GetOrdersResDto> & {
        onInvalidPagination?: () => void;
    } = {}
) => {
    const pageSize = params.pageSize || 5;

    const { data, ...other } = useInfiniteQuery({
        queryKey: [useOrdersLazyKey, params],
        queryFn: async ({ pageParam }) => {
            const response = await OrdersService.getOrders({
                query: {
                    ...(params.action && { action: params.action }),
                    ...(params.at_start && { at_start: params.at_start }),
                    ...(params.at_end && { at_end: params.at_end }),
                    ...(params.by && { by: params.by }),
                    ...(params.statuses && { statuses: params.statuses }),
                    ...(params.text_search && {
                        text_search: params.text_search,
                    }),
                    ...(params.bookings !== undefined && {
                        bookings: params.bookings,
                    }),
                    ...(pageParam?.page !== undefined && {
                        page_number: pageParam.page,
                    }),
                    ...(pageSize && { page_size: pageSize }),
                    ...(pageParam?.startAtId && {
                        start_at_id: pageParam.startAtId,
                    }),
                },
            });

            if (!response.data) throw new Error('Failed to get orders');

            return response.data;
        },
        getNextPageParam: lastPage => {
            if (!lastPage.items || !lastPage.next_id) return undefined;
            return {
                startAtId: lastPage.next_id,
                page: lastPage.next_page_number,
            };
        },
        ...options,
        onError: (error: any) => {
            if (error?.status === 409) {
                options.onInvalidPagination?.();
            } else options.onError?.(error);
        },
    });

    const totalCount = data?.pages.find(page => page.total_count)?.total_count;
    const parsedData = useMemo(
        () =>
            data?.pages
                ? data.pages
                      .map(page =>
                          (page.items || []).map(getOrderItemDtoToOrder)
                      )
                      .reduce((arr, cur) => [...arr, ...cur], [])
                : undefined,
        [data]
    );

    return {
        totalCount,
        data: parsedData,
        ...other,
    };
};

export const useOrderKey = 'useOrderKey';
export const useOrder = (
    id?: string,
    options: CustomQueryOptions<GetOrderResDto> = {}
) => {
    const { data, ...other } = useQuery({
        queryKey: [useOrderKey, id],
        queryFn: async () => {
            const response = await OrdersService.getOrder({
                path: { order_id: id as string },
            });

            if (!response.data) throw new Error('Failed to get order');

            return response.data;
        },
        enabled: !!id,
        ...options,
    });

    return {
        data: useMemo(
            () => (data ? getOrderItemDtoToOrder(data) : data),
            [data]
        ),
        ...other,
    };
};

export const useUpdateOrderBooking = () => {
    const queryClient = useQueryClient();
    const queriesData = queryClient.getQueriesData({
        queryKey: ['OrdersServiceGetOrder'],
        exact: false,
    });

    const update = (
        bookingId: string,
        callback: (
            b: GetEventResBookingsItem0Dto | GetEventResBookingsItem1Dto
        ) => GetEventResBookingsItem0Dto | GetEventResBookingsItem1Dto
    ) => {
        queriesData.forEach(([queryKey]) => {
            queryClient.setQueryData<GetOrderResDto>(
                queryKey,
                // @ts-ignore
                order => {
                    if (!order) return undefined;
                    const bookings =
                        order.bookings?.map(b =>
                            b.id === bookingId ? callback(b) : b
                        ) || [];
                    return {
                        ...order,
                        bookings,
                    };
                }
            );
        });
    };

    return update;
};
